import React from 'react';
import { CheckCircle, Target, Award, Users } from 'lucide-react';
import { companyInfo, stats } from '../data/mock';
import { Reveal } from '../components/Reveal';

const About = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1767960816704-69682c77c6e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhdmlhdGlvbiUyMGFpcmNyYWZ0fGVufDB8fHx8MTc3MTA0NTY1OHww&ixlib=rb-4.1.0&q=85"
            alt="About ONEAVIATION"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal y={30}>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About ONEAVIATION</h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
              {companyInfo.tagline} - Leading the way in aviation excellence
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <Reveal x={-40} y={0} className="bg-slate-800 rounded-2xl p-8 border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-slate-300" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-slate-300 leading-relaxed">
                To provide comprehensive, reliable, and innovative aviation solutions that exceed our clients' expectations. We strive to be the trusted partner for all aviation needs, delivering excellence in leasing, sales, services, and MRO operations.
              </p>
            </Reveal>

            <Reveal x={40} y={0} className="bg-slate-800 rounded-2xl p-8 border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center mb-6">
                <Award className="text-slate-300" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-slate-300 leading-relaxed">
                To be recognized as the leading aviation solutions provider in the region, known for our commitment to safety, quality, and customer satisfaction. We envision a future where aviation services are seamless, efficient, and accessible to all.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal x={-40} y={0}>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Founded with a vision to revolutionize aviation services, ONEAVIATION LLP has grown from a small operation to a comprehensive aviation solutions provider serving clients across the globe.
                </p>
                <p>
                  With over 15 years of industry experience, we have successfully managed 50+ aircraft, completed 100+ projects, and built lasting relationships with leading airlines and operators worldwide.
                </p>
                <p>
                  Our team of certified professionals brings unparalleled expertise in aircraft leasing, sales, maintenance, and technical services, ensuring that every client receives world-class support tailored to their unique needs.
                </p>
                <p>
                  Today, ONEAVIATION stands as a trusted name in aviation, committed to safety, quality, and innovation in everything we do.
                </p>
              </div>
            </Reveal>
            <Reveal x={40} y={0} className="relative">
              <img
                src="https://images.unsplash.com/photo-1760183957226-8ad1e94c863f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBhdmlhdGlvbiUyMGFpcmNyYWZ0fGVufDB8fHx8MTc3MTA0NTY1OHww&ixlib=rb-4.1.0&q=85"
                alt="Our Story"
                className="rounded-2xl shadow-2xl"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Safety First',
                description: 'Safety is our top priority in every operation, ensuring the highest standards of airworthiness and compliance.'
              },
              {
                title: 'Excellence',
                description: 'We strive for excellence in every service we provide, exceeding industry standards and client expectations.'
              },
              {
                title: 'Integrity',
                description: 'Honesty and transparency guide our relationships with clients, partners, and stakeholders.'
              },
              {
                title: 'Innovation',
                description: 'We embrace new technologies and methodologies to deliver cutting-edge aviation solutions.'
              },
              {
                title: 'Customer Focus',
                description: 'Our clients are at the heart of everything we do, and their success is our success.'
              },
              {
                title: 'Reliability',
                description: '24/7 support and consistent delivery make us a dependable partner you can trust.'
              }
            ].map((value, index) => (
              <Reveal
                key={index}
                delay={index * 0.06}
                className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20 hover:-translate-y-2"
              >
                <CheckCircle className="text-slate-400 mb-4" size={32} />
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Achievements</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Reveal key={index} delay={index * 0.08} className="text-center group">
                <div className="text-5xl md:text-6xl font-bold text-chrome mb-3 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-base md:text-lg">{stat.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Certifications & Compliance</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              We maintain the highest industry standards and certifications
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'ISO 9001:2015',
              'EASA Certified',
              'DGCA Approved',
              'Part 145 Certified'
            ].map((cert, index) => (
              <Reveal
                key={index}
                delay={index * 0.08}
                className="bg-slate-800 rounded-xl p-6 text-center border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20"
              >
                <Award className="mx-auto text-slate-400 mb-3" size={40} />
                <p className="text-white font-semibold">{cert}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
